import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Quote, TrendingUp } from "lucide-react";
import { SITE } from "@/lib/site-config";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const absUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const scrollToForm = () => {
  const el = document.getElementById("lead-form");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ErfolgeSection = () => {
  const { stats } = SITE.successes;
  const [stories, setStories] = useState([]);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/stories`, { timeout: 12000 })
      .then((r) => { if (active) setStories(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (active) setStories([]); });
    return () => { active = false; };
  }, []);

  const gridCols =
    stories.length === 1
      ? "grid-cols-1 max-w-md mx-auto"
      : stories.length === 2
      ? "grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto"
      : "grid-cols-1 md:grid-cols-3";

  return (
    <section
      id="resultate"
      className="relative py-20 md:py-32 border-t border-[#556340] overflow-hidden"
      data-testid="erfolge-section"
    >
      {/* Subtle glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[300px] rounded-full bg-[#E8E5D0]/[0.05] blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E8E5D0] mb-4">
            Erfolge unserer Teilnehmer
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
            Echte Menschen.
            <br />
            <span className="lime-gradient-text">Echte Resultate.</span>
          </h2>
          <p className="mt-5 text-[#C9C6B4] max-w-2xl">
            Gewicht verloren, Muskeln aufgebaut, Gesundheit verbessert – hinter
            jedem Erfolg steht klare Struktur, ehrliches Feedback und
            konsequente Betreuung.
          </p>
        </motion.div>

        {/* Stats */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="rounded-2xl border border-white/12 bg-[#4E5A3B]/60 p-5"
              data-testid={`erfolge-stat-${i}`}
            >
              <div className="font-display text-3xl sm:text-4xl font-black lime-gradient-text leading-none">
                {s.value}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[#A8A58F]">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Story Cards */}
        {stories.length > 0 && (
          <div className={`mt-12 grid gap-5 ${gridCols}`}>
            {stories.map((story, idx) => {
              const before = absUrl(story.before_image);
              const after = absUrl(story.after_image);
              const collage = absUrl(story.collage_image);
              const isCollage = story.layout === "collage" && collage;
              const bothSeparate = before && after;
              const singleImg = !isCollage && !bothSeparate ? after || before : null;
              const singleIsAfter = !!after;
              const hasText = story.title || story.description || story.customer_name || story.coaching_duration;
              return (
                <motion.div
                  key={story.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: idx * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="group rounded-2xl border border-[rgba(255,255,255,0.14)] bg-[#4E5A3B]/60 hover:bg-[#4E5A3B]/80 hover:border-[#E8E5D0]/40 transition-colors overflow-hidden flex flex-col"
                  data-testid={`erfolge-story-${idx}`}
                >
                  {/* Variant A: finished collage — shown whole (contain), never split */}
                  {isCollage ? (
                    <div className="relative bg-[#3A4230]">
                      <img
                        src={collage}
                        alt={`${story.customer_name || "Transformation"} – Vorher / Nachher`}
                        className="w-full h-auto object-contain"
                        loading="lazy"
                        data-testid={`erfolge-story-${idx}-collage`}
                      />
                      <div className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.2em] bg-black/70 backdrop-blur-md text-white px-2 py-1 rounded">
                        Vorher
                      </div>
                      <div className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-[0.2em] bg-[#E8E5D0] text-[#3A4230] px-2 py-1 rounded">
                        Nachher
                      </div>
                    </div>
                  ) : bothSeparate ? (
                    /* Variant B: two separate images side by side */
                    <div className="relative grid grid-cols-2 aspect-[4/3]">
                      <div className="relative overflow-hidden">
                        <img
                          src={before}
                          alt={`${story.customer_name || "Transformation"} – Vorher`}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-[0.7] transition-all duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.2em] bg-black/70 backdrop-blur-md text-white px-2 py-1 rounded">
                          Vorher
                        </div>
                      </div>
                      <div className="relative overflow-hidden border-l border-[#E8E5D0]/30">
                        <img
                          src={after}
                          alt={`${story.customer_name || "Transformation"} – Nachher`}
                          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-[0.2em] bg-[#E8E5D0] text-[#3A4230] px-2 py-1 rounded">
                          Nachher
                        </div>
                      </div>
                    </div>
                  ) : singleImg ? (
                    /* Only one image active — single result image, no fake comparison */
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={singleImg}
                        alt={`${story.customer_name || "Transformation"} – ${singleIsAfter ? "Ergebnis" : "Vorher"}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className={`absolute top-3 ${singleIsAfter ? "right-3 bg-[#E8E5D0] text-[#3A4230]" : "left-3 bg-black/70 backdrop-blur-md text-white"} text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded`}>
                        {singleIsAfter ? "Ergebnis" : "Vorher"}
                      </div>
                    </div>
                  ) : null}

                  {/* Content — only rendered when text exists (no empty areas) */}
                  {hasText && (
                    <div className="p-6 flex-1 flex flex-col">
                      {story.title && (
                        <div className="flex items-center gap-2 text-[#E8E5D0]">
                          <TrendingUp size={14} />
                          <span className="text-[11px] font-bold uppercase tracking-[0.18em]">
                            {story.title}
                          </span>
                        </div>
                      )}
                      {story.description && (
                        <>
                          <Quote size={20} className="text-[#E8E5D0]/30 mt-4 -mb-1" aria-hidden="true" />
                          <p className="text-[#E5E2D0] text-[15px] leading-relaxed">
                            {story.description}
                          </p>
                        </>
                      )}
                      {(story.customer_name || story.coaching_duration) && (
                        <div className="mt-auto pt-5 flex items-baseline justify-between gap-3 border-t border-[#556340]">
                          {story.customer_name ? (
                            <div className="font-display font-black text-[#F3F1E8] tracking-tight">
                              {story.customer_name}
                            </div>
                          ) : <span />}
                          {story.coaching_duration && (
                            <div className="text-[11px] uppercase tracking-[0.18em] text-[#A8A58F]">
                              {story.coaching_duration}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Conversion CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-14 text-center"
        >
          <button
            onClick={scrollToForm}
            className="group inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider px-7 py-4 rounded-full transition-colors"
            data-testid="erfolge-cta-btn"
          >
            Werde der nächste Erfolg
            <ArrowRight
              size={18}
              className="group-hover:translate-x-1 transition-transform"
            />
          </button>
          <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[#A8A58F]">
            Antwort innerhalb von 24 Stunden · Unverbindlich
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ErfolgeSection;
